import React, { forwardRef, useEffect, useState } from 'react'
import {
  Datepicker as KittenDatePicker,
  NativeDateService,
} from '@ui-kitten/components'
import moment from 'moment'
import { observer } from 'mobx-react'
import { EvaInputSize, RenderProp } from '@ui-kitten/components/devsupport'
import { ImageProps, ViewStyle } from 'react-native'
import { composeStyle } from '../helpers'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { colors } from '../theme'
import { useResponsive } from '../hooks'
import { BtInputLabel } from './BtInputLabel'

const formatDateService = new NativeDateService('en', {
  format: 'MM/DD/YYYY',
  startDayOfWeek: 1,
})

interface Props {
  onChange?: (date: moment.Moment | null | string | Date) => void
  model?: any
  name?: string
  value?: string | moment.Moment
  min?: string | moment.Moment
  max?: string | moment.Moment
  label?: string
  placeholder?: string
  rightComponent?: RenderProp<Partial<ImageProps>>
  leftComponent?: RenderProp<Partial<ImageProps>>
  testID?: string
  noIcon?: boolean
  clearIcon?: boolean
  viewFormat?: string
  returnFormat?: 'moment' | 'date' | 'MM/DD/YYYY' | 'YYYY-MM-DDD'
  mandatory?: boolean
  style?: ViewStyle
  onFocus?: () => void
  onBlur?: () => void
  size?: EvaInputSize
}

const DB_DATE_FORMAT = 'YYYY-MM-DD'

function getReturnValue(date, format) {
  const _date = moment(date)
  if (!format) return _date
  if (format === 'date') return _date.toDate()
  if (format === 'moment') return _date
  return _date.format(format)
}

export const BtDatePicker = observer(
  forwardRef(
    (
      {
        placeholder = ' ',
        onChange,
        model,
        name,
        value,
        label,
        min,
        max,
        rightComponent,
        leftComponent,
        testID,
        noIcon = false,
        clearIcon = true,
        mandatory = false,
        returnFormat = 'MM/DD/YYYY',
        onBlur,
        onFocus,
        style,
        size,
        ...rest
      }: Props,
      ref: React.Ref<KittenDatePicker>,
    ) => {
      const [localValue, setLocalValue] = useState<moment.Moment | null>(null)

      const { sm } = useResponsive()

      useEffect(() => {
        if (value) {
          setLocalValue(moment(value))
        } else if (model && name && model[name]) {
          setLocalValue(moment(model[name]))
        }
        return () => setLocalValue(null)
        //eslint-disable-next-line react-hooks/exhaustive-deps
      }, [value])

      useEffect(() => {
        let val = localValue
        if (model && name) {
          val = model[name]
          if (!val && value) {
            model.setValue(name, value)
          }
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
      }, [localValue])

      const setDate = (date) => {
        const dt = date ? getReturnValue(date, returnFormat) : undefined
        if (model && name) {
          if (model.setValue) {
            model.setValue(name, dt)
          } else {
            model[name] = dt
          }
        }
        if (onChange) {
          onChange(dt || null)
        }

        setLocalValue(moment(dt))
      }

      const CalendarIcon = (props) => {
        const iconProps = {
          ...props,
          size: 20,
          style: { position: 'relative' },
          color: colors.primary,
        }

        return clearIcon && localValue ? (
          <MaterialCommunityIcons
            {...iconProps}
            onPress={() => setDate(null)}
            name={'close'}
          />
        ) : noIcon ? (
          <></>
        ) : (
          <MaterialCommunityIcons {...iconProps} name={'calendar-month'} />
        )
      }

      return (
        <KittenDatePicker
          testID={testID}
          placeholder={placeholder}
          min={
            min
              ? moment(min).toDate()
              : moment('1940-01-01', DB_DATE_FORMAT).toDate()
          }
          max={
            max
              ? moment(max).toDate()
              : moment('2040-01-01', DB_DATE_FORMAT).toDate()
          }
          label={<BtInputLabel required={mandatory}>{label}</BtInputLabel>}
          date={localValue ? localValue.toDate() : undefined}
          size={size ? size : sm ? 'large' : 'medium'}
          onSelect={(dt) => {
            setDate(dt)
          }}
          accessoryRight={rightComponent ? rightComponent : CalendarIcon}
          onBlur={onBlur}
          onFocus={onFocus}
          accessoryLeft={leftComponent && leftComponent}
          dateService={formatDateService}
          style={composeStyle({ marginTop: 16 }, style)}
          ref={ref}
          {...rest}
        />
      )
    },
  ),
)
